import React, { useEffect, useRef, useState } from 'react';

import Tippy from '@tippyjs/react';
import { useRecoilValue } from 'recoil';

import { convertMarkdownToHTML, iaRa } from 'lib/helpers/utility';

import { copyToClipboard } from 'components/admin/pages/Practices/lib/utility';
import Icon from 'components/shared/Icon/Icon';

import { generalChatActivePromptConversationsState, generalChatLoading } from './state';

const GeneratingText = () => {
  const [dots, setDots] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length < 3 ? prev + '.' : ''));
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex w-full flex-col gap-3 rounded-lg">
      <div className="text-base font-600 text-neutral-800">
        Generating
        <span className="w-5 text-base font-400">{dots}</span>
      </div>
    </div>
  );
};

export const ChatMessages = () => {
  const messages = useRecoilValue(generalChatActivePromptConversationsState);
  const messagesEndRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState();
  const loading = useRecoilValue(generalChatLoading);

  const closeModal = () => setSelectedImage(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="relative flex-1 overflow-y-auto p-4">
      <div className="space-y-4">
        {iaRa(messages)?.map((msg, idx) => (
          <div
            key={msg.id}
            className={`flex ${msg.agent === 'user' ? 'flex-col items-end' : 'flex-col justify-start'}`}>
            <div
              className={`max-w-md rounded-lg p-3 ${
                msg.agent === 'user' ? 'bg-primary-400 text-white' : 'bg-gray-200 text-gray-800'
              }`}>
              <div className="mb-2 flex items-center justify-end gap-2">
                {iaRa(msg.attachments)?.map((attachment) => (
                  <div key={attachment?.url} className="mb-2 flex justify-end">
                    {attachment?.type?.includes('image') ? (
                      <img
                        src={attachment.url}
                        width={100}
                        height={100}
                        className="cursor-pointer rounded"
                        onClick={() => setSelectedImage(attachment.url)}
                      />
                    ) : (
                      <div className="inline-block rounded bg-neutral-100 p-1 text-black">
                        <a
                          href={attachment.url}
                          download={attachment.name}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-blue-800 underline">
                          {attachment?.name}
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div
                className="whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(msg.message) }}
              />
            </div>
            {msg.agent === 'ai' && (
              <div className="flex max-w-md justify-end">
                <Tippy className="tippy-dark" content="Copy" placement="left" arrow={false}>
                  <div>
                    <Icon
                      icon="copy-linear"
                      onClick={() => copyToClipboard(navigator, msg.message)}
                    />
                  </div>
                </Tippy>
              </div>
            )}
            {loading && idx === messages?.length - 1 && msg.agent === 'user' && (
              <div className="my-4 w-full pl-2">
                <GeneratingText />
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
          onClick={closeModal}>
          <div className="relative max-h-[90vh] max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <Icon
              color="white"
              size={20}
              icon="close-thick-linear"
              onClick={closeModal}
              className="fixed right-5 top-5"
            />
            <img
              src={selectedImage}
              alt="Full Size"
              className="z-50 max-h-[90vh] max-w-full rounded"
            />
          </div>
        </div>
      )}
    </div>
  );
};
